import { z } from 'zod';

/**
 * A monetary value is inseparable from the evidence behind it.
 *
 * `unknown` deliberately cannot carry an amount. This is the type-level guard
 * that prevents a missing provider charge from turning into a billed-looking
 * `$0` at a rendering call site.
 */
export type CostValue =
  | { amount: number; provenance: 'billed' | 'estimate' }
  | { amount: null; provenance: 'unknown' };

export type CostProvenance = CostValue['provenance'];

export const costValueSchema = z.discriminatedUnion('provenance', [
  z.object({
    amount: z.number().nonnegative(),
    provenance: z.literal('billed'),
  }),
  z.object({
    amount: z.number().nonnegative(),
    provenance: z.literal('estimate'),
  }),
  z.object({
    amount: z.null(),
    provenance: z.literal('unknown'),
  }),
]);

export const unknownCost = (): CostValue => ({ amount: null, provenance: 'unknown' });

export function billedCost(amount: number): CostValue {
  return { amount, provenance: 'billed' };
}

export function estimatedCost(amount: number): CostValue {
  return { amount, provenance: 'estimate' };
}

/**
 * Reconstruct a value at a serialization boundary. An unknown source always
 * discards the serialized placeholder amount.
 */
export function costValueFromParts(
  amount: number | null | undefined,
  provenance: CostProvenance | 'none' | null | undefined,
): CostValue {
  if (provenance === 'unknown' || provenance === 'none' || amount == null) {
    return unknownCost();
  }
  return provenance === 'billed' ? billedCost(amount) : estimatedCost(amount);
}

/**
 * Conservative aggregate: one unknown constituent makes the total unknown;
 * otherwise one estimate makes the total an estimate.
 */
export function addCostValues(values: readonly CostValue[]): CostValue {
  if (values.some((value) => value.provenance === 'unknown')) return unknownCost();
  const amount = values.reduce((sum, value) => sum + (value.amount ?? 0), 0);
  return values.some((value) => value.provenance === 'estimate')
    ? estimatedCost(amount)
    : billedCost(amount);
}

/** Legacy event/storage representation used while old rows remain readable. */
export function costProvenanceToSource(
  provenance: CostProvenance,
): 'billed' | 'estimate' | 'none' {
  return provenance === 'unknown' ? 'none' : provenance;
}
